import {
  UTIL_ES_PRESET_WEIGHTS,
  UTIL_PRESET_WEIGHTS,
  type UtilisationWeightPreset,
  type UtilisationWeightsUi,
  weightsTotal,
} from '../../utils/utilisationFormulaWeights';

interface UtilisationFormulaWeightsSectionProps {
  serviceType: 'logistics' | 'emergency_services';
  weights: UtilisationWeightsUi;
  preset: UtilisationWeightPreset;
  disabled?: boolean;
  /** When locked, first interaction enters edit mode instead of looking disabled. */
  onActivateEdit?: () => void;
  onPresetChange: (preset: UtilisationWeightPreset, weights: UtilisationWeightsUi) => void;
  onWeightsChange: (weights: UtilisationWeightsUi) => void;
}

export default function UtilisationFormulaWeightsSection({
  serviceType,
  weights,
  preset,
  disabled = false,
  onActivateEdit,
  onPresetChange,
  onWeightsChange,
}: UtilisationFormulaWeightsSectionProps) {
  const emergency = serviceType === 'emergency_services';
  const total = weightsTotal(weights, emergency);
  const locked = disabled && Boolean(onActivateEdit);
  const hardDisabled = disabled && !onActivateEdit;

  const activateOr = (action: () => void) => {
    if (locked) {
      onActivateEdit?.();
      return;
    }
    if (hardDisabled) return;
    action();
  };

  return (
    <section className="space-y-3">
      <div>
        <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">
          Formula weights
        </h4>
        <p className="text-xs text-muted-foreground mt-1">
          How much each efficiency component contributes to Overall Util %. Saved with targets when
          you click Save (truth compute uses these weights).
          {locked ? ' Click a preset or slider to start editing.' : ''}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {emergency
          ? (
              [
                { id: 'balanced' as const, label: 'Balanced (50/50)' },
                { id: 'distance' as const, label: 'Miles-first (70/30)' },
                { id: 'es_time' as const, label: 'Time-first (30/70)' },
                { id: 'custom' as const, label: 'Custom' },
              ] as const
            ).map(({ id, label }) => (
              <button
                key={id}
                type="button"
                disabled={hardDisabled}
                onClick={() =>
                  activateOr(() => {
                    const presetWeights = id !== 'custom' ? UTIL_ES_PRESET_WEIGHTS[id] : null;
                    onPresetChange(id, presetWeights ? { ...presetWeights } : weights);
                  })
                }
                className={`min-w-26 rounded-lg border px-3 py-2 text-center text-xs transition-all disabled:opacity-50 ${
                  preset === id
                    ? 'border-chart-10 bg-chart-10/10 text-chart-10'
                    : 'border-border bg-card/50 text-foreground hover:border-border'
                }`}
              >
                {label}
              </button>
            ))
          : (
              [
                { id: 'balanced' as const, label: 'Balanced' },
                { id: 'revenue' as const, label: 'Revenue' },
                { id: 'distance' as const, label: 'Distance' },
                { id: 'custom' as const, label: 'Custom' },
              ] as const
            ).map(({ id, label }) => (
              <button
                key={id}
                type="button"
                disabled={hardDisabled}
                onClick={() =>
                  activateOr(() => {
                    const presetWeights = id !== 'custom' ? UTIL_PRESET_WEIGHTS[id] : null;
                    onPresetChange(id, presetWeights ? { ...presetWeights } : weights);
                  })
                }
                className={`min-w-26 rounded-lg border px-3 py-2 text-center text-xs transition-all disabled:opacity-50 ${
                  preset === id
                    ? 'border-chart-10 bg-chart-10/10 text-chart-10'
                    : 'border-border bg-card/50 text-foreground hover:border-border'
                }`}
              >
                {label}
              </button>
            ))}
      </div>

      <div className="space-y-2">
        {emergency
          ? (
              [
                { key: 'milesEff' as const, label: 'Miles' },
                { key: 'timeEff' as const, label: 'Time' },
              ] as const
            ).map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-xs text-foreground w-16 shrink-0">{label}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  disabled={hardDisabled}
                  value={weights[key]}
                  onPointerDown={(e) => {
                    if (locked) {
                      e.preventDefault();
                      onActivateEdit?.();
                    }
                  }}
                  onFocus={() => {
                    if (locked) onActivateEdit?.();
                  }}
                  onChange={(e) => {
                    if (locked || hardDisabled) return;
                    const newValue = parseInt(e.target.value, 10);
                    if (Number.isNaN(newValue)) return;
                    const next =
                      key === 'milesEff'
                        ? {
                            milesEff: newValue,
                            timeEff: 100 - newValue,
                            revEff: 0,
                            loadEff: 0,
                          }
                        : {
                            milesEff: 100 - newValue,
                            timeEff: newValue,
                            revEff: 0,
                            loadEff: 0,
                          };
                    onWeightsChange(next);
                    onPresetChange('custom', next);
                  }}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-secondary disabled:opacity-50"
                />
                <span className="text-xs font-medium text-white tabular-nums w-10 text-right">
                  {weights[key]}%
                </span>
              </div>
            ))
          : (
              [
                { key: 'milesEff' as const, label: 'Miles' },
                { key: 'revEff' as const, label: 'Revenue' },
                { key: 'timeEff' as const, label: 'Time' },
                { key: 'loadEff' as const, label: 'Load' },
              ] as const
            ).map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-xs text-foreground w-16 shrink-0">{label}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  disabled={hardDisabled}
                  value={weights[key]}
                  onPointerDown={(e) => {
                    if (locked) {
                      e.preventDefault();
                      onActivateEdit?.();
                    }
                  }}
                  onFocus={() => {
                    if (locked) onActivateEdit?.();
                  }}
                  onChange={(e) => {
                    if (locked || hardDisabled) return;
                    const newValue = parseInt(e.target.value, 10);
                    const others = (['milesEff', 'revEff', 'timeEff', 'loadEff'] as const).filter(
                      (k) => k !== key,
                    );
                    const totalOther = others.reduce((s, k) => s + weights[k], 0);
                    const remaining = 100 - newValue;
                    let next: UtilisationWeightsUi;
                    if (remaining >= 0 && totalOther > 0) {
                      const scale = remaining / totalOther;
                      const a = Math.round(weights[others[0]] * scale);
                      const b = Math.round(weights[others[1]] * scale);
                      const c = remaining - a - b;
                      next = {
                        ...weights,
                        [key]: newValue,
                        [others[0]]: a,
                        [others[1]]: b,
                        [others[2]]: c,
                      };
                    } else {
                      next = { ...weights, [key]: newValue };
                    }
                    onWeightsChange(next);
                    onPresetChange('custom', next);
                  }}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-secondary disabled:opacity-50"
                />
                <span className="text-xs font-medium text-white tabular-nums w-10 text-right">
                  {weights[key]}%
                </span>
              </div>
            ))}
      </div>

      <div className="flex justify-end">
        <span
          className={`text-xs font-medium tabular-nums ${
            total === 100 ? 'text-chart-10' : 'text-warning'
          }`}
        >
          Total: {total}%
        </span>
      </div>
    </section>
  );
}
